// Builds a single shareable PNG of the whole trip: every day's stops pinned on
// one map, colour-coded by day, with route lines, a legend and branding.
// Self-contained (no map API key): composites CORS-enabled OpenStreetMap tiles
// onto a canvas so the resulting image is downloadable / shareable.
// (Switched off CARTO basemaps — they now watermark keyless tiles with
// "API KEY REQUIRED".)

const TS = 256; // OpenStreetMap standard tile size in px
const DAY_COLORS = ["#DF3226", "#1E3A8A", "#0E9F6E", "#E8590C", "#7C3AED", "#0891B2"];
// November/December trips get the Christmas palette — red, pine green, gold.
const FESTIVE_DAY_COLORS = ["#DF3226", "#0E5C3A", "#B7791F", "#7A1420", "#125C40", "#C9A227"];

// A string of glowing Christmas lights draped across the top of a canvas.
export const drawLightsString = (ctx, W, y = 64, sag = 44) => {
  ctx.save();
  ctx.strokeStyle = "rgba(10,10,10,0.55)";
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(-10, y);
  ctx.quadraticCurveTo(W / 2, y + sag * 2, W + 10, y);
  ctx.stroke();
  const BULBS = ["#DF3226", "#FDE047", "#7ED957", "#6FC3FF", "#FF9F1C"];
  const N = 14;
  for (let i = 0; i <= N; i++) {
    const t = i / N;
    const x = (1 - t) * (1 - t) * -10 + 2 * (1 - t) * t * (W / 2) + t * t * (W + 10);
    const yy = (1 - t) * (1 - t) * y + 2 * (1 - t) * t * (y + sag * 2) + t * t * y;
    ctx.globalAlpha = 0.25; // glow
    ctx.fillStyle = BULBS[i % BULBS.length];
    ctx.beginPath();
    ctx.arc(x, yy + 11, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1; // bulb
    ctx.beginPath();
    ctx.arc(x, yy + 11, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(10,10,10,0.8)"; // cap
    ctx.fillRect(x - 3, yy - 2, 6, 5);
  }
  ctx.restore();
};

// A cheerful three-ball snowman with carrot nose, red scarf and stick arms.
export const drawSnowman = (ctx, x, y, s = 1) => {
  ctx.save();
  ctx.globalAlpha = 0.18; // soft ground shadow
  ctx.fillStyle = "#0A0A0A";
  ctx.beginPath();
  ctx.ellipse(x, y + 40 * s, 46 * s, 10 * s, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.fillStyle = "#FFFFFF";
  ctx.beginPath(); ctx.arc(x, y, 42 * s, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(x, y - 58 * s, 30 * s, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(x, y - 102 * s, 21 * s, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#0A0A0A"; // eyes + buttons
  ctx.beginPath(); ctx.arc(x - 7 * s, y - 106 * s, 2.5 * s, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(x + 7 * s, y - 106 * s, 2.5 * s, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(x, y - 62 * s, 3 * s, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(x, y - 48 * s, 3 * s, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#E8590C"; // carrot
  ctx.beginPath();
  ctx.moveTo(x, y - 101 * s);
  ctx.lineTo(x + 17 * s, y - 97 * s);
  ctx.lineTo(x, y - 93 * s);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "#DF3226"; // scarf
  ctx.fillRect(x - 22 * s, y - 86 * s, 44 * s, 9 * s);
  ctx.fillRect(x + 8 * s, y - 80 * s, 9 * s, 22 * s);
  ctx.strokeStyle = "#7A4A21"; // stick arms
  ctx.lineWidth = 3 * s;
  ctx.lineCap = "round";
  ctx.beginPath(); ctx.moveTo(x - 26 * s, y - 64 * s); ctx.lineTo(x - 54 * s, y - 82 * s); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(x + 26 * s, y - 64 * s); ctx.lineTo(x + 54 * s, y - 82 * s); ctx.stroke();
  ctx.restore();
};

// A wrapped present with a gold ribbon and bow.
export const drawPresent = (ctx, x, y, s = 1, color = "#DF3226") => {
  ctx.save();
  ctx.fillStyle = color;
  ctx.fillRect(x - 40 * s, y - 34 * s, 80 * s, 68 * s); // box
  ctx.fillRect(x - 47 * s, y - 48 * s, 94 * s, 16 * s); // lid
  ctx.fillStyle = "#FDE047";
  ctx.fillRect(x - 8 * s, y - 48 * s, 16 * s, 82 * s); // ribbon
  ctx.beginPath(); ctx.arc(x - 13 * s, y - 55 * s, 11 * s, 0, Math.PI * 2); ctx.fill(); // bow
  ctx.beginPath(); ctx.arc(x + 13 * s, y - 55 * s, 11 * s, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
};

// Rudolph — a simple reindeer face with branched antlers and a glowing red nose.
export const drawReindeer = (ctx, x, y, s = 1) => {
  ctx.save();
  ctx.strokeStyle = "#7A4A21"; // antlers
  ctx.lineWidth = 5 * s;
  ctx.lineCap = "round";
  [-1, 1].forEach((d) => {
    ctx.beginPath(); ctx.moveTo(x + d * 14 * s, y - 24 * s); ctx.lineTo(x + d * 26 * s, y - 58 * s); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x + d * 21 * s, y - 44 * s); ctx.lineTo(x + d * 36 * s, y - 54 * s); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(x + d * 17 * s, y - 34 * s); ctx.lineTo(x + d * 5 * s, y - 46 * s); ctx.stroke();
  });
  ctx.fillStyle = "#9C6B3D"; // head
  ctx.beginPath(); ctx.arc(x, y, 30 * s, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#0A0A0A"; // eyes
  ctx.beginPath(); ctx.arc(x - 10 * s, y - 6 * s, 3 * s, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.arc(x + 10 * s, y - 6 * s, 3 * s, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#DF3226"; // glowing red nose
  ctx.globalAlpha = 0.3;
  ctx.beginPath(); ctx.arc(x, y + 14 * s, 15 * s, 0, Math.PI * 2); ctx.fill();
  ctx.globalAlpha = 1;
  ctx.beginPath(); ctx.arc(x, y + 14 * s, 8 * s, 0, Math.PI * 2); ctx.fill();
  ctx.restore();
};

const project = (lat, lon, z) => {
  const world = TS * Math.pow(2, z);
  const x = ((lon + 180) / 360) * world;
  const latRad = (lat * Math.PI) / 180;
  const y =
    ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * world;
  return { x, y };
};

const loadTile = (z, x, y) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;
  });

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

// plan: array of days, each { day, area, items:[{name, coords:[lat,lon]}] }
export async function buildTripMapImage(plan, host) {
  const days = plan
    .map((d) => ({
      ...d,
      pts: (d.items || []).filter((s) => Array.isArray(s.coords)),
    }))
    .filter((d) => d.pts.length);

  const allStops = days.flatMap((d) => d.pts);
  if (!allStops.length) return null;

  const W = 1080;
  const H = 1920;
  const padX = 70;
  const padTop = 580; // leave room for the title block + Instagram's top safe zone
  const padBottom = 400; // leave room for legend + branding + IG's bottom safe zone
  const availW = W - padX * 2;
  const availH = H - padTop - padBottom;

  // pick the tightest zoom that still fits every stop in the content area
  // (17 on standard tiles ≈ the detail the old @2x tiles gave at 16)
  let z = 17;
  for (; z >= 8; z--) {
    const p = allStops.map((s) => project(s.coords[0], s.coords[1], z));
    const spanX = Math.max(...p.map((q) => q.x)) - Math.min(...p.map((q) => q.x));
    const spanY = Math.max(...p.map((q) => q.y)) - Math.min(...p.map((q) => q.y));
    if (spanX <= availW && spanY <= availH) break;
  }

  const proj = allStops.map((s) => project(s.coords[0], s.coords[1], z));
  const cx = (Math.max(...proj.map((q) => q.x)) + Math.min(...proj.map((q) => q.x))) / 2;
  const cy = (Math.max(...proj.map((q) => q.y)) + Math.min(...proj.map((q) => q.y))) / 2;
  // centre the cluster inside the content band (between title and legend)
  const originX = cx - W / 2;
  const originY = cy - (padTop + availH / 2);

  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // base fill (in case a tile fails)
  ctx.fillStyle = "#E8ECEF";
  ctx.fillRect(0, 0, W, H);

  // fetch + draw the map tiles that cover the canvas
  const worldTiles = Math.pow(2, z);
  const txMin = Math.floor(originX / TS);
  const txMax = Math.floor((originX + W) / TS);
  const tyMin = Math.floor(originY / TS);
  const tyMax = Math.floor((originY + H) / TS);
  const jobs = [];
  for (let tx = txMin; tx <= txMax; tx++) {
    for (let ty = tyMin; ty <= tyMax; ty++) {
      if (ty < 0 || ty >= worldTiles) continue;
      const wx = ((tx % worldTiles) + worldTiles) % worldTiles;
      jobs.push(
        loadTile(z, wx, ty).then((img) => {
          if (img) ctx.drawImage(img, tx * TS - originX, ty * TS - originY, TS, TS);
        })
      );
    }
  }
  await Promise.all(jobs);

  // soften the map so pins pop
  ctx.fillStyle = "rgba(249,248,246,0.18)";
  ctx.fillRect(0, 0, W, H);

  const toXY = (s) => {
    const p = project(s.coords[0], s.coords[1], z);
    return { x: p.x - originX, y: p.y - originY };
  };

  // route line + numbered pins, per day
  days.forEach((d, di) => {
    const color = DAY_COLORS[di % DAY_COLORS.length];
    const xy = d.pts.map(toXY);
    if (xy.length > 1) {
      ctx.strokeStyle = color;
      ctx.lineWidth = 5;
      ctx.globalAlpha = 0.7;
      ctx.setLineDash([2, 12]);
      ctx.lineCap = "round";
      ctx.beginPath();
      xy.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }
    xy.forEach((p, i) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 22, 0, Math.PI * 2);
      ctx.fillStyle = "#fff";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(p.x, p.y, 19, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.font = "800 22px 'Plus Jakarta Sans', sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(String(i + 1), p.x, p.y + 1);
    });
  });
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";

  // title block — pushed down into Instagram's safe zone (top ~250px is covered)
  ctx.fillStyle = "#0A0A0A";
  ctx.font = "900 40px 'Plus Jakarta Sans', sans-serif";
  ctx.fillText("CPH Highlights.", padX, 290);
  ctx.font = "800 20px 'Nunito Sans', sans-serif";
  ctx.fillStyle = "rgba(10,10,10,0.5)";
  ctx.fillText("THE MODERN GUIDE", padX, 322);
  ctx.fillStyle = "#0A0A0A";
  ctx.font = "900 92px 'Plus Jakarta Sans', sans-serif";
  const isEdt = plan.some((d) => d.edt);
  ctx.fillText(isEdt ? "My EDT to" : "My Copenhagen", padX, 424);
  ctx.fillStyle = "#DF3226";
  ctx.fillText(isEdt ? "Copenhagen" : "itinerary", padX, 424 + 96);

  // legend (bottom band) — with many days the long "Day N · Area · Area" labels
  // wrap onto extra rows and collide with the footer, so compact mode kicks in:
  // smaller type, no area names, tighter rows
  const many = days.length > 4;
  const legFont = many ? 18 : 22;
  const legRowH = many ? 42 : 52;
  const dotR = many ? 11 : 13;
  const legY = H - padBottom + 30;
  ctx.fillStyle = "#0A0A0A";
  let lx = padX;
  let ly = legY;
  days.forEach((d, di) => {
    const color = DAY_COLORS[di % DAY_COLORS.length];
    const label = many ? `Day ${d.day}` : `Day ${d.day}${d.area ? ` · ${d.area}` : ""}`;
    ctx.font = `800 ${legFont}px 'Nunito Sans', sans-serif`;
    const w = 40 + ctx.measureText(label).width + (many ? 20 : 34);
    if (lx + w > W - padX) {
      lx = padX;
      ly += legRowH;
    }
    ctx.beginPath();
    ctx.arc(lx + 14, ly - (many ? 6 : 7), dotR, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.fillStyle = "#0A0A0A";
    ctx.fillText(label, lx + 38, ly);
    lx += w;
  });

  // footer — lifted up out of Instagram's bottom safe zone (bottom ~250px is covered)
  ctx.textAlign = "center";
  ctx.fillStyle = "#0A0A0A";
  ctx.font = "800 28px 'Nunito Sans', sans-serif";
  ctx.fillText(`Plan yours at ${host}`, W / 2, H - 260);
  // OSM licence: the map-data credit must ride along on the image
  ctx.font = "600 16px 'Nunito Sans', sans-serif";
  ctx.fillStyle = "rgba(10,10,10,0.45)";
  ctx.fillText("© OpenStreetMap contributors", W / 2, H - 228);
  ctx.textAlign = "left";

  return await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

// Draws a small plan map into a rect of an existing canvas — the Story image
// uses it as a bottom strip when the day list leaves room. Same keyless OSM
// tiles and day colours as the full trip map. Returns false when there's
// nothing with coords to draw.
export async function drawMiniMap(ctx, plan, x, y, w, h, festive = false) {
  const colors = festive ? FESTIVE_DAY_COLORS : DAY_COLORS;
  const days = plan
    .map((d, i) => ({
      ...d,
      color: colors[i % colors.length],
      pts: (d.items || []).filter((s) => Array.isArray(s.coords)),
    }))
    .filter((d) => d.pts.length);
  const allStops = days.flatMap((d) => d.pts);
  if (!allStops.length) return false;

  let z = 17;
  for (; z >= 8; z--) {
    const p = allStops.map((s) => project(s.coords[0], s.coords[1], z));
    const spanX = Math.max(...p.map((q) => q.x)) - Math.min(...p.map((q) => q.x));
    const spanY = Math.max(...p.map((q) => q.y)) - Math.min(...p.map((q) => q.y));
    if (spanX <= w && spanY <= h) break;
  }
  const proj = allStops.map((s) => project(s.coords[0], s.coords[1], z));
  const cx = (Math.max(...proj.map((q) => q.x)) + Math.min(...proj.map((q) => q.x))) / 2;
  const cy = (Math.max(...proj.map((q) => q.y)) + Math.min(...proj.map((q) => q.y))) / 2;
  const originX = cx - w / 2;
  const originY = cy - h / 2;

  ctx.save();
  roundRect(ctx, x, y, w, h, 36);
  ctx.clip();
  ctx.fillStyle = "#E8ECEF";
  ctx.fillRect(x, y, w, h);

  const worldTiles = Math.pow(2, z);
  const txMin = Math.floor(originX / TS);
  const txMax = Math.floor((originX + w) / TS);
  const tyMin = Math.floor(originY / TS);
  const tyMax = Math.floor((originY + h) / TS);
  const jobs = [];
  for (let tx = txMin; tx <= txMax; tx++) {
    for (let ty = tyMin; ty <= tyMax; ty++) {
      if (ty < 0 || ty >= worldTiles) continue;
      const wx = ((tx % worldTiles) + worldTiles) % worldTiles;
      jobs.push(
        loadTile(z, wx, ty).then((img) => {
          if (img) ctx.drawImage(img, x + tx * TS - originX, y + ty * TS - originY, TS, TS);
        })
      );
    }
  }
  await Promise.all(jobs);
  ctx.fillStyle = "rgba(249,248,246,0.12)";
  ctx.fillRect(x, y, w, h);

  const toXY = (s) => {
    const p = project(s.coords[0], s.coords[1], z);
    return { x: x + p.x - originX, y: y + p.y - originY };
  };
  days.forEach((d) => {
    const xy = d.pts.map(toXY);
    if (xy.length > 1) {
      ctx.strokeStyle = d.color;
      ctx.lineWidth = 4;
      ctx.globalAlpha = 0.75;
      ctx.setLineDash([2, 10]);
      ctx.lineCap = "round";
      ctx.beginPath();
      xy.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }
    xy.forEach((p) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 13, 0, Math.PI * 2);
      ctx.fillStyle = "#fff";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(p.x, p.y, 10.5, 0, Math.PI * 2);
      ctx.fillStyle = d.color;
      ctx.fill();
    });
  });
  // OSM licence credit, tiny, bottom-right corner of the map
  ctx.font = "600 15px 'Nunito Sans', sans-serif";
  ctx.textAlign = "right";
  ctx.fillStyle = "rgba(10,10,10,0.5)";
  ctx.fillText("© OpenStreetMap", x + w - 12, y + h - 10);
  ctx.textAlign = "left";
  ctx.restore();
  return true;
}
